import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Building2, Server, Users, Coins, Receipt, Clock, Wifi, WifiOff, Plus, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/shared/StatCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGetDashboardStats,
  useGetRevenueChart,
  useGetRecentTransactions,
  useGetTopMachines,
} from '@/services/api/endpoints/DashboardService';
import { formatAmount, formatDate } from '@/lib/utils';
import { RoutesConfig } from '@/navigation/routes';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  type TooltipProps,
} from 'recharts';

function RevenueTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border bg-card p-3 shadow-lg">
      <p className="mb-1 text-sm font-medium">{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-3 text-sm">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">
            {entry.dataKey === 'revenue' ? 'Prihod' : 'Transakcije'}
          </span>
          <span className="ml-auto font-medium">
            {entry.dataKey === 'revenue' ? formatAmount(entry.value as number) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useGetDashboardStats();

  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const { data: revenueData, isLoading: revenueLoading } = useGetRevenueChart(period);
  const { data: recentTx, isLoading: txLoading } = useGetRecentTransactions(10);
  const { data: topMachines, isLoading: topLoading } = useGetTopMachines();

  const chartData = useMemo(() => {
    if (!revenueData) return [];
    return revenueData.map(item => ({
      ...item,
      dateFormatted: item.date.length > 7
        ? new Date(item.date).toLocaleDateString('sr-Latn', { day: 'numeric', month: 'short' })
        : item.date,
    }));
  }, [revenueData]);

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const onlineMachines = stats?.activeMachines || 0;
  const offlineMachines = (stats?.totalMachines || 0) - onlineMachines;
  const top5Machines = topMachines?.slice(0, 5) || [];

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">{t('dashboard.title')}</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => navigate(RoutesConfig.Companies.fullPath)}>
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">{t('companies.createCompany')}</span>
            <span className="sm:hidden">{t('companies.company')}</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(RoutesConfig.Machines.fullPath)}>
            <Plus className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">{t('machines.createMachine')}</span>
            <span className="sm:hidden">{t('machines.machine')}</span>
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('dashboard.totalCompanies')}
          value={stats?.totalCompanies || 0}
          icon={Building2}
          iconColor="purple"
        />
        <StatCard
          title={t('dashboard.totalMachines')}
          value={stats?.totalMachines || 0}
          subtitle={`${onlineMachines} ${t('dashboard.onlineMachines').toLowerCase()}`}
          icon={Server}
          iconColor="blue"
        />
        <StatCard
          title={t('dashboard.transactionsToday')}
          value={stats?.totalTransactions || 0}
          icon={Receipt}
          iconColor="green"
        />
        <StatCard
          title={t('dashboard.revenueToday')}
          value={formatAmount(stats?.totalRevenue || 0)}
          icon={Coins}
          iconColor="yellow"
        />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title={t('dashboard.totalUsers')}
          value={stats?.totalUsers || 0}
          icon={Users}
          iconColor="blue"
        />
        <StatCard
          title={t('dashboard.activeSessions')}
          value={stats?.pendingSessions || 0}
          icon={Clock}
          iconColor="green"
        />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('dashboard.machineStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Wifi className="w-5 h-5 text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{onlineMachines}</p>
                  <p className="text-sm text-muted-foreground">{t('dashboard.onlineMachines')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-danger/10 flex items-center justify-center">
                  <WifiOff className="w-5 h-5 text-danger" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{offlineMachines}</p>
                  <p className="text-sm text-muted-foreground">{t('dashboard.offlineMachines')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Chart */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-2">
          <CardTitle className="text-base">{t('dashboard.revenueChart')}</CardTitle>
          <div className="flex gap-1">
            {(['daily', 'weekly', 'monthly'] as const).map((p) => (
              <Button
                key={p}
                variant={period === p ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setPeriod(p)}
              >
                {t(`revenue.${p}`)}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {revenueLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-72 flex items-center justify-center border border-dashed rounded-lg bg-muted/20 text-muted-foreground text-sm">
              {t('common.noResults')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={288}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="dateFormatted"
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => {
                    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value.toString();
                  }}
                />
                <Tooltip content={<RevenueTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: 'hsl(var(--primary))' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Bottom Row: Recent Transactions + Top Machines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">{t('dashboard.recentTransactions')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(RoutesConfig.Transactions.fullPath)}>
              {t('common.all')}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {txLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : !recentTx?.length ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {t('common.noResults')}
              </div>
            ) : (
              <div className="divide-y">
                {recentTx.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {tx.productName || tx.machine?.name || '-'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {tx.user?.email || '-'} &middot; {formatDate(tx.createdAt)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <span className="text-sm font-medium">{formatAmount(tx.amount)}</span>
                      <StatusBadge status={tx.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Machines by Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-500" />
              {t('dashboard.topMachines')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(RoutesConfig.Revenue.fullPath)}>
              {t('common.all')}
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {topLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : !top5Machines.length ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                {t('common.noResults')}
              </div>
            ) : (
              <div className="divide-y">
                {top5Machines.map((machine, index) => (
                  <div key={machine.machineId} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0 text-xs">
                        {index + 1}
                      </Badge>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{machine.machineName}</p>
                        <p className="text-xs text-muted-foreground">{machine.companyName}</p>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-sm font-medium">{formatAmount(machine.revenue)}</p>
                      <p className="text-xs text-muted-foreground">
                        {machine.transactionCount} tx
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
